import { useRef, useState } from "react";
import WhiteUploadButton from "src/Components/Common/whiteuploadbutton";
import TicketService from "src/Services/API/ticket.service";
import UserService from "src/Services/API/user.service";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

const NewTicket = () => {
  const navigate = useNavigate();
  const currentUser = UserService.getCurrentUser();
  const fileInputRef = useRef(null);
  const subjectRef = useRef(null);
  const workOrderRef = useRef(null);
  const purchaseOrderRef = useRef(null);
  const descriptionRef = useRef(null);
  const [screenshotFiles, setScreenshotFiles] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const handleRemoveFile = (fileToRemove) => {
    setScreenshotFiles((prev) => prev.filter((f) => f !== fileToRemove));
  };

  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    setScreenshotFiles((prev) => [...prev, ...files]);
    // Reset the input so the same file can be selected again if needed
    e.target.value = "";
  };

  const handleSubmit = async () => {
    setSubmitError(null);
    const subject = subjectRef.current?.value?.trim();
    const description = descriptionRef.current?.value?.trim();
    if (!subject || !description) {
      setSubmitError("Subject and Description are required.");
      return;
    }
    setIsSubmitting(true);
    try {

      const workOrderNumber = workOrderRef.current?.value ?? "";
      const purchaseOrderNumber = purchaseOrderRef.current?.value ?? "";

      const payload = {
        status: 0,
        createdByUserId: currentUser?.id,
        requesterUserId: currentUser?.id,
        platform: "Service platform",
      };

      const trimmedSubject = subject.trim();
      const trimmedDescription = description.trim();
      const trimmedWoNumber = workOrderNumber.trim();
      const trimmedPoNumber = purchaseOrderNumber.trim();

      if (trimmedSubject) payload.subject = trimmedSubject;
      if (trimmedDescription) payload.description = trimmedDescription;
      if (trimmedWoNumber) payload.woNumber = trimmedWoNumber;
      if (trimmedPoNumber) payload.poNumber = trimmedPoNumber;

      const response = await TicketService.save(payload);
      // Upload screenshots if any were attached
      if (screenshotFiles.length > 0) {
        const ticketId = response?.data?.id;
        if (ticketId) {
          await TicketService.uploadAttachments(screenshotFiles, ticketId);
        }
      }
      // Clear form on success
      if (subjectRef.current) subjectRef.current.value = "";
      if (workOrderRef.current) workOrderRef.current.value = "";
      if (purchaseOrderRef.current) purchaseOrderRef.current.value = "";
      if (descriptionRef.current) descriptionRef.current.value = "";
      setScreenshotFiles([]);
      //handle success
      toast.success("Ticket submitted successfully");
      navigate("/ticket");
    } catch (err) {
      setSubmitError(err?.response?.data?.message || err?.message || "Failed to submit ticket.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="w-full px-5 py-6">
      <div className="mx-auto w-full max-w-[760px]">
        <div className="flex flex-col gap-2">
          <span className="font-OpenSansRegular font-[700] text-[12px] text-titleColor">
            Subject
          </span>
          <input
            ref={subjectRef}
            type="text"
            placeholder="Couldn't submit service ticket"
            className="h-[34px] rounded-[6px] border border-borderColor px-3 font-OpenSansRegular text-[12px] text-titleColor placeholder:text-subTitleColor focus:outline-none"
          />
        </div>

        <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2">
          <label className="flex flex-col gap-2">
            <span className="font-OpenSansRegular font-[700] text-[12px] text-titleColor">
              Work Order #
            </span>
            <input
              ref={workOrderRef}
              type="text"
              placeholder="12345"
              className="h-[34px] rounded-[6px] border border-borderColor px-3 font-OpenSansRegular text-[12px] text-titleColor placeholder:text-subTitleColor focus:outline-none"
            />
          </label>
          <label className="flex flex-col gap-2">
            <span className="font-OpenSansRegular font-[700] text-[12px] text-titleColor">
              Purchase Order #
            </span>
            <input
              ref={purchaseOrderRef}
              type="text"
              placeholder="12345"
              className="h-[34px] rounded-[6px] border border-borderColor px-3 font-OpenSansRegular text-[12px] text-titleColor placeholder:text-subTitleColor focus:outline-none"
            />
          </label>
        </div>

        <div className="mt-5 flex flex-col gap-2">
          <span className="font-OpenSansRegular font-[700] text-[12px] text-titleColor">
            Description
          </span>
          <textarea
            ref={descriptionRef}
            placeholder="Couldn't submit service ticket, app froze"
            className="min-h-[100px] rounded-[6px] border border-borderColor px-3 py-2 font-OpenSansRegular text-[12px] text-titleColor placeholder:text-subTitleColor focus:outline-none"
          />
        </div>

        <div className="mt-5">
          <span className="font-OpenSansRegular font-[700] text-[12px] text-titleColor">
            Add screenshots (optional)
          </span>
          <div className="mt-3 flex flex-col gap-2">
            {screenshotFiles.map((file, index) => (
              <div
                key={`${file.name}-${index}`}
                className="flex items-center justify-between gap-3 rounded-[6px] border border-borderColor px-3 py-1"
              >
                <span className="truncate font-OpenSansRegular text-[12px] text-titleColor">
                  {file.name}
                </span>
                <button
                  type="button"
                  onClick={() => handleRemoveFile(file)}
                  className="font-OpenSansRegular text-[10px] font-[600] text-subTitleColor"
                >
                  REMOVE
                </button>
              </div>
            ))}
          </div>

          <div className="mt-4">
            <WhiteUploadButton clicked={() => fileInputRef.current?.click()} isLoading={false} />
            <input ref={fileInputRef} type="file" accept="image/*" multiple onChange={handleFileUpload} className="hidden" />
          </div>

          <p className="mt-3 font-OpenSansRegular text-[12px] text-subTitleColor">
            Your app version, email and phone number will be included in the ticket. This will help us resolve
            the bug.
          </p>
        </div>

        {submitError && (
          <p className="mt-4 font-OpenSansRegular text-[12px] text-red-500">{submitError}</p>
        )}
        <div className="mt-8 flex justify-end">
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="h-[36px] min-w-[110px] rounded-[6px] bg-blueColor px-4 font-OpenSansRegular text-[12px] font-[600] text-white disabled:opacity-60"
          >
            {isSubmitting ? "Submitting…" : "REPORT"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default NewTicket;